import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../lib/api-client';
import { useAuth } from '../hooks/useAuth';
import { usePageMeta } from '../lib/page-meta';
import AuthAside from '../components/layout/AuthAside';
import Button from '../components/ui/Button';

type VerificationState = 'idle' | 'verifying' | 'invalid' | 'verified';

interface ResendContext {
  email: string;
  organisationSlug: string;
}

interface ResendVerificationResponse {
  accepted: true;
  verificationEmail: {
    deliveryStatus: 'sent' | 'skipped' | 'failed';
    deliveryReason: string | null;
    expiresAt: string | null;
  };
}

export default function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    pendingVerification,
    hasPendingVerification,
    resendVerificationEmail,
    confirmEmailVerification,
    clearPendingVerification,
    isLoading,
  } = useAuth();

  usePageMeta({
    title: t('verifyEmail.metaTitle'),
    description: t('verifyEmail.metaDescription'),
    canonicalPath: '/verify-email',
    robots: 'noindex,nofollow',
  });

  const [verificationState, setVerificationState] = useState<VerificationState>(token ? 'verifying' : 'idle');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendContext, setResendContext] = useState<ResendContext | null>(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !token) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, token]);

  useEffect(() => {
    if (!token) {
      return;
    }
    const run = async () => {
      setVerificationState('verifying');
      setErrorMessage(null);
      try {
        const result = await confirmEmailVerification(token);
        setVerificationState('verified');
        setFeedback(t('verifyEmail.success'));
        navigate(result.nextPath, { replace: true });
      } catch (error) {
        setVerificationState('invalid');
        if (error instanceof ApiError) {
          setErrorMessage(error.message);
          const details = error.details as { email?: unknown; organisationSlug?: unknown } | undefined;
          if (
            typeof details?.email === 'string' &&
            typeof details?.organisationSlug === 'string'
          ) {
            setResendContext({
              email: details.email,
              organisationSlug: details.organisationSlug,
            });
          }
        } else {
          setErrorMessage(t('verifyEmail.errors.invalid'));
        }
      }
    };

    void run();
  }, [confirmEmailVerification, navigate, t, token]);

  const activeResendContext = useMemo<ResendContext | null>(() => {
    if (pendingVerification) {
      return {
        email: pendingVerification.user.email,
        organisationSlug: pendingVerification.organisation.slug,
      };
    }
    return resendContext;
  }, [pendingVerification, resendContext]);

  const handleResend = async () => {
    setFeedback(null);
    setErrorMessage(null);
    setResending(true);
    try {
      if (pendingVerification) {
        await resendVerificationEmail();
      } else if (activeResendContext) {
        await api.post<ResendVerificationResponse>(
          '/auth/verify-email/resend',
          activeResendContext,
          { skipAuth: true },
        );
      } else {
        throw new Error('No resend context available');
      }

      setFeedback(t('verifyEmail.resendSuccess'));
    } catch (error) {
      if (error instanceof ApiError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(t('verifyEmail.errors.resend'));
      }
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    clearPendingVerification();
    navigate('/login', { replace: true });
  };

  const deliveryStatus = pendingVerification?.verificationEmail.deliveryStatus ?? 'skipped';
  const verificationEmail = pendingVerification?.user.email ?? resendContext?.email ?? '';
  const verificationWorkspace = pendingVerification?.organisation.name ?? '';

  return (
    <div className="acc-auth-page">
      <div className="acc-auth-shell">
        <AuthAside />

        <div className="acc-auth-card acc-auth-card--panel">
          <div className="acc-auth-panel__header">
            <div className="acc-auth-panel__eyebrow">{t('verifyEmail.eyebrow')}</div>
            <h1 className="acc-auth-card__title">{t('verifyEmail.title')}</h1>
            <p className="acc-auth-card__description">
              {verificationWorkspace
                ? t('verifyEmail.descriptionWithWorkspace', {
                    email: verificationEmail,
                    workspace: verificationWorkspace,
                  })
                : t('verifyEmail.description')}
            </p>
          </div>

          {verificationState === 'verifying' && (
            <div className="acc-auth-banner acc-auth-banner--info">
              {t('verifyEmail.verifying')}
            </div>
          )}

          {feedback && (
            <div className="acc-auth-banner acc-auth-banner--success">
              {feedback}
            </div>
          )}

          {errorMessage && (
            <div role="alert" className="acc-auth-banner acc-auth-banner--danger">
              {errorMessage}
            </div>
          )}

          {!token && hasPendingVerification && (
            <div className="acc-auth-form" style={{ gap: 18 }}>
              <div className="acc-auth-banner acc-auth-banner--info">
                {deliveryStatus === 'sent'
                  ? t('verifyEmail.delivery.sent', { email: verificationEmail })
                  : deliveryStatus === 'failed'
                    ? t('verifyEmail.delivery.failed')
                    : t('verifyEmail.delivery.pending', { email: verificationEmail })}
              </div>

              <div className="acc-auth-form__group">
                <label htmlFor="verify-email-address" className="acc-auth-form__label">{t('verifyEmail.emailLabel')}</label>
                <input id="verify-email-address" className="acc-input" value={verificationEmail} readOnly />
              </div>

              <div className="acc-auth-form__actions">
                <Button type="button" size="lg" disabled={resending || isLoading} onClick={handleResend} style={{ width: '100%' }}>
                  {resending ? t('verifyEmail.resending') : t('verifyEmail.resend')}
                </Button>
              </div>

              <div className="acc-auth-secondary-actions" data-testid="verify-email-secondary-actions">
                <button
                  type="button"
                  className="acc-link-inline acc-auth-secondary-actions__button"
                  onClick={() => {
                    handleBackToLogin();
                  }}
                >
                  {t('verifyEmail.useDifferentAccount')}
                </button>
                <button
                  type="button"
                  className="acc-link-inline acc-auth-secondary-actions__link"
                  onClick={handleBackToLogin}
                >
                  {t('verifyEmail.backToLogin')}
                </button>
              </div>
            </div>
          )}

          {!token && !hasPendingVerification && verificationState !== 'verifying' && (
            <div className="acc-auth-form" style={{ gap: 18 }}>
              <div className="acc-auth-banner acc-auth-banner--info">
                {t('verifyEmail.noPendingState')}
              </div>
              <div className="acc-auth-form__actions">
                <button type="button" className="acc-link-inline" onClick={handleBackToLogin}>
                  {t('verifyEmail.backToLogin')}
                </button>
              </div>
            </div>
          )}

          {token && verificationState === 'invalid' && (
            <div className="acc-auth-form" style={{ gap: 18 }}>
              <div className="acc-auth-banner acc-auth-banner--warning">
                {t('verifyEmail.invalidLink')}
              </div>
              {activeResendContext && (
                <div className="acc-auth-form__actions">
                  <Button type="button" size="lg" disabled={resending || isLoading} onClick={handleResend} style={{ width: '100%' }}>
                    {resending ? t('verifyEmail.resending') : t('verifyEmail.resend')}
                  </Button>
                </div>
              )}
              <div className="acc-auth-footer acc-auth-footer--panel">
                <button type="button" className="acc-link-inline" onClick={handleBackToLogin}>
                  {t('verifyEmail.backToLogin')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
